import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AiActions } from "../../../Services/_actions/Ai/ai.actions.ts";
import Timer from "../Timer.tsx";
import QuestionCard from "../QuestionCard.tsx";
import ConfirmBox from "../../FlowBuilder/Common/ConfirmBox/index.tsx";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Block as BlockIcon,
  Timer as TimerIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

const TestPage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();

  const { getTestApi, startTestApi, submitTestApi } = AiActions();

  const [test, setTest] = useState<any>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState<"submit" | "clear" | "timeout" | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);

  // Security features state
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [violationLog, setViolationLog] = useState<Array<{ type: string, timestamp: string }>>([]);

  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const violationLogRef = useRef<Array<{ type: string, timestamp: string }>>([]);
  const tabSwitchCountRef = useRef<number>(0);

  // ✅ Fetch test data WITHOUT show_answers parameter
  const fetchTestData = async (queryParams) => {
    try {
      setLoading(true);
      const response = await getTestApi(queryParams);
      if (response?.status === 200 && response?.data) {
        setTest(response?.data);
      } else {
        setTest(null);
        console.error("❌ Failed to fetch test data.");
      }
    } catch (error) {
      console.error("❌ Failed to fetch test data.", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!testId) return;
    fetchTestData({ testId: testId, showAnswers: 0 });
  }, [testId]);

  const total = test?.questions?.length ?? 0;
  const attempted = Object.keys(answers).length;
  const remaining = total - attempted;

  // ✅ Tab switch detection
  useEffect(() => {
    if (!started) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        const timestamp = new Date().toLocaleTimeString();

        const newViolation = { type: "Tab Switch", timestamp };
        violationLogRef.current = [...violationLogRef.current, newViolation];
        tabSwitchCountRef.current = tabSwitchCountRef.current + 1;

        setViolationLog(violationLogRef.current);

        setTabSwitchCount((prev) => {
          const newCount = prev + 1;

          if (newCount === 1) {
            setWarningMessage("⚠️ Warning: Tab switching detected! This is your first warning.");
            setShowTabWarning(true);
            setSnackbarOpen(true);
          } else if (newCount >= 2) {
            setWarningMessage("🚫 Test auto-submitted due to multiple tab switches!");
            setSnackbarOpen(true);
            setTimeout(() => {
              handleAutoSubmit();
            }, 1000);
          }

          return newCount;
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [started]);

  // ✅ Prevent copy-paste
  useEffect(() => {
    if (!started) return;

    const preventCopyPaste = (e: Event) => {
      e.preventDefault();
      const timestamp = new Date().toLocaleTimeString();
      const eventType = e.type === "copy" ? "Copy Attempt" : e.type === "cut" ? "Cut Attempt" : "Paste Attempt";

      const newViolation = { type: eventType, timestamp };
      violationLogRef.current = [...violationLogRef.current, newViolation];

      setViolationLog(violationLogRef.current);
      setWarningMessage(`⚠️ ${eventType.replace(" Attempt", "")} operation is disabled during the test!`);
      setSnackbarOpen(true);
    };

    const preventContextMenu = (e: Event) => {
      e.preventDefault();
      const timestamp = new Date().toLocaleTimeString();

      const newViolation = { type: "Right-Click Attempt", timestamp };
      violationLogRef.current = [...violationLogRef.current, newViolation];

      setViolationLog(violationLogRef.current);
      setWarningMessage("⚠️ Right-click is disabled during the test!");
      setSnackbarOpen(true);
    };

    document.addEventListener("copy", preventCopyPaste);
    document.addEventListener("cut", preventCopyPaste);
    document.addEventListener("paste", preventCopyPaste);
    document.addEventListener("contextmenu", preventContextMenu);

    return () => {
      document.removeEventListener("copy", preventCopyPaste);
      document.removeEventListener("cut", preventCopyPaste);
      document.removeEventListener("paste", preventCopyPaste);
      document.removeEventListener("contextmenu", preventContextMenu);
    };
  }, [started]);

  // ✅ Start test
  const handleStartTest = async () => {
    if (!testId) return;

    setStarting(true);

    try {
      const response = await startTestApi({ test_id: testId });
      if (response?.status === 200 && response?.data?.data?.start_time) {
        setStartTime(response?.data?.data?.start_time);
        setStarted(true);
        setElapsedSeconds(0);
      }
    } catch (error) {
      console.error("Failed to start test:", error);
      alert("❌ Failed to start test.");
    } finally {
      setStarting(false);
    }
  };

  const handleSelect = (qid: string, option: string) => {
    setAnswers((prev) => ({ ...prev, [qid]: option }));
  };

  const handleClearQuestion = (qid: string) => {
    setAnswers((prev) => {
      const copy = { ...prev };
      delete copy[qid];
      return copy;
    });
  };

  const computeTimeTakenFromStart = (start: number | null) => {
    if (!start) return 0;
    const now = Date.now();
    const startMs = start > 1e12 ? start : start * 1000;
    return Math.max(0, Math.floor((now - startMs) / 1000));
  };

  const handleSubmit = async () => {
    if (!test || !startTime) return;
    setSubmitting(true);
    try {
      const time_taken =
        elapsedSeconds && elapsedSeconds > 0
          ? elapsedSeconds
          : computeTimeTakenFromStart(startTime);

      const payload = {
        test_id: test?.test_id,
        user_answers: answers,
        time_taken,
        violation_log: violationLogRef.current,
        tab_switch_count: tabSwitchCountRef.current,
        total_violations: violationLogRef.current.length,
      };

      console.log("Submitting test with payload:", payload);

      const res = await submitTestApi(payload);

      navigate(`/r/test/result/${test?.test_id}`, {
        state: {
          answers: answers,
          time_taken: time_taken,
          violations: violationLogRef.current,
          tabSwitchCount: tabSwitchCountRef.current,
          test: test
        }
      });
    } catch (error) {
      console.error("Failed to submit test:", error);
      alert("⚠️ Failed to submit test.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoSubmit = async () => {
    if (!test || !startTime) return;
    setSubmitting(true);
    try {
      const time_taken =
        elapsedSeconds && elapsedSeconds > 0
          ? elapsedSeconds
          : computeTimeTakenFromStart(startTime);

      const payload = {
        test_id: test?.test_id,
        user_answers: answers,
        time_taken,
        violation_log: violationLogRef.current,
        tab_switch_count: tabSwitchCountRef.current,
        total_violations: violationLogRef.current.length,
        auto_submitted: true,
      };

      console.log("Auto-submitting test with payload:", payload);

      await submitTestApi(payload);

      navigate(`/r/test/result/${test?.test_id}`, {
        state: {
          answers: answers,
          time_taken: time_taken,
          autoSubmitted: true,
          violations: violationLogRef.current,
          tabSwitchCount: tabSwitchCountRef.current
        }
      });
    } catch (error) {
      console.error("Failed to auto-submit test:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const openConfirm = (type: "submit" | "clear" | "timeout") => {
    setConfirmType(type);
    setConfirmOpen(true);
  };

  const handleConfirmClose = (confirmed: boolean) => {
    setConfirmOpen(false);
    if (!confirmed) return;

    if (confirmType === "submit" || confirmType === "timeout") handleSubmit();
    if (confirmType === "clear") setAnswers({});
  };

  const scrollToQuestion = (qid: string) => {
    const el = questionRefs.current[qid];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading)
    return (
      <Box textAlign="center" mt={8}>
        <CircularProgress />
        <Typography mt={2}>Loading test...</Typography>
      </Box>
    );

  if (!test) {
    return (
      <Box textAlign="center" mt={10}>
        <Typography variant="h6" color="error">
          ❌ Test not available.
        </Typography>
      </Box>
    );
  }

  // ✅ Already submitted case
  if (test?.test_submitted) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f0f4ff 0%, #e3f2fd 100%)",
          p: 2,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, sm: 5 },
            textAlign: "center",
            borderRadius: 5,
            maxWidth: 520,
            width: "100%",
            backdropFilter: "blur(12px)",
            background: "rgba(255,255,255,0.85)",
          }}
        >
          <Typography variant="h4" fontWeight={700} color="primary.main" mb={2}>
            Test Already Submitted
          </Typography>

          <Typography variant="body1" color="text.secondary" mb={3}>
            You've already completed and submitted this test.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 4,
              fontStyle: "italic",
            }}
          >
            Review your results to see how you performed.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() =>
              navigate(`/r/test/result/${test?.test_id}`, {
                state: { from: "test_page" },
              })
            }
            sx={{
              px: 5,
              py: 1.5,
              borderRadius: "50px",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            📊 View My Result
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!started)
    return (
      <Box
        sx={{
          minHeight: "85vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #eef2ff 0%, #e0f7fa 100%)",
        }}
      >
        <Paper
          elevation={8}
          sx={{
            backdropFilter: "blur(12px)",
            background: "rgba(255, 255, 255, 0.95)",
            borderRadius: 3,
            p: 4,
            maxWidth: 900,
            width: "100%",
            mx: 2,
          }}
        >
          {/* Header - Compact */}
          <Box sx={{ textAlign: "center", mb: 2.5 }}>
            <Typography variant="h4" fontWeight={700} color="primary.main" mb={0.5}>
              {test?.filled_fields?.test_type || test?.filled_fields?.topic || "Your Test Awaits"}{test?.filled_fields?.subject ? ` - ${test.filled_fields.subject}` : ""}
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={1.5}>
              {/* {test?.subject && `Subject: ${test.subject}`} */}
              {test?.subject && test?.class && " • "}
              {test?.class && `Class ${test.class}`}
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={1.5}>
              {test?.filled_fields?.subject && `Subject: ${test.filled_fields.subject}`}
              {test?.filled_fields?.subject && test?.filled_fields?.class && " • "}
              {test?.filled_fields?.class && `Class ${test.filled_fields.class}`}
            </Typography>
            <Stack direction="row" justifyContent="center" spacing={1.5}>
              <Chip
                label={`Total Questions: ${total}`}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={`Duration: ${test?.test_time ?? 10} min`}
                color="secondary"
                variant="outlined"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </Box>

          {/* Instructions - Ultra Compact */}
          <Box
            sx={{
              bgcolor: "#f8f9ff",
              borderRadius: 2,
              p: 2.5,
              border: "2px solid",
              borderColor: "primary.light",
              mb: 2.5,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <WarningIcon color="primary" sx={{ fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600} color="primary.main">
                Instructions & Guidelines
              </Typography>
            </Stack>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BlockIcon color="error" sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>
                      No Tab Switching (1st = warning, 2nd = auto-submit)
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BlockIcon color="error" sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>
                      No Copy-Paste
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VisibilityIcon color="primary" sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>
                      Stay Focused & Keep this tab active
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckIcon color="success" sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>
                      Review answers before submitting
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <WarningIcon color="warning" sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>
                      Breaking rules = auto-submit current answers
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TimerIcon color="primary" sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>
                      Timer starts immediately, no pause
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          {/* Start Button - Compact */}
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={1.5}
              sx={{ fontStyle: "italic" }}
            >
              Once started, timer begins immediately. You cannot pause or restart.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleStartTest}
              disabled={starting}
              sx={{
                px: 5,
                py: 1.3,
                fontSize: "0.95rem",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "50px",
                boxShadow: "0 4px 14px rgba(63,81,181,0.4)",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(63,81,181,0.45)",
                },
              }}
            >
              {starting ? "Starting Test..." : "🚀 I Understand, Start Test"}
            </Button>
          </Box>
        </Paper>
      </Box>
    );

  // ✅ Main test interface
  return (
    <Box sx={{ userSelect: "none" }}>
      {/* Header */}
      <Box sx={{ p: 1.5, mb: 1, boxShadow: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Typography variant="h6" fontWeight={700}>
            {test?.filled_fields?.test_type || "Test"}{test?.filled_fields?.subject ? ` - ${test.filled_fields.subject}` : ""}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {test?.subject && `Subject: ${test.subject}`}
            {test?.subject && test?.class && " • "}
            {test?.class && `Class ${test.class}`}
          </Typography>


          <Stack direction="row" spacing={2} alignItems="center">
            {tabSwitchCount > 0 && (
              <Chip
                label={`⚠️ Warnings: ${tabSwitchCount}/2`}
                color={tabSwitchCount === 1 ? "warning" : "error"}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
            {violationLog.length > 0 && (
              <Chip
                label={`🚫 Violations: ${violationLog.length}`}
                color="error"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
            <Timer
              // timeLimit={10}
              timeLimit={test?.test_time ?? 10}
              onExpire={() => openConfirm("timeout")}
              onTick={(elapsed) => setElapsedSeconds(elapsed)}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => openConfirm("submit")}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        {/* Questions */}
        <Grid item xs={12} md={8}>
          <Box sx={{ height: "75vh", overflowY: "auto", pr: 1 }}>
            {test.questions.map((q: any, i: number) => (
              <div key={q.question_id} ref={(el) => (questionRefs.current[q.question_id] = el)}>
                <QuestionCard
                  q={q}
                  index={i}
                  selected={answers[q.question_id]}
                  onSelect={handleSelect}
                  onClear={handleClearQuestion}
                  testSubmitted={test?.test_submitted}
                />
              </div>
            ))}
          </Box>
        </Grid>

        {/* Progress + Navigator */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, boxShadow: 2, position: "sticky", top: 16 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={700}>
                Progress
              </Typography>

              <Stack direction="row" spacing={1}>
                <Chip label={`Total: ${total}`} size="small" sx={{ bgcolor: "#f5f5f5" }} />
                <Chip label={`✔ ${attempted}`} size="small" color="primary" />
                <Chip
                  label={`⏳ ${remaining}`}
                  size="small"
                  sx={{ bgcolor: "#e0e0e0", color: "black" }}
                />
              </Stack>
            </Stack>

            <Typography
              variant="subtitle1"
              fontWeight={600}
              mb={1}
              sx={{ color: "text.secondary" }}
            >
              Question Navigator
            </Typography>

            <Grid container spacing={1} justifyContent="center">
              {test.questions.map((q: any, i: number) => {
                const attemptedQ = !!answers[q.question_id];
                return (
                  <Grid item key={q.question_id}>
                    <Button
                      onClick={() => scrollToQuestion(q.question_id)}
                      sx={{
                        minWidth: "unset",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        p: 0,
                        m: 0.4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        bgcolor: attemptedQ ? "primary.main" : "#f5f5f5",
                        color: attemptedQ ? "white" : "black",
                        "&:hover": {
                          bgcolor: attemptedQ ? "primary.dark" : "#e0e0e0",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      {i + 1}
                    </Button>
                  </Grid>
                );
              })}
            </Grid>

            {/* Violation Log */}
            {violationLog.length > 0 && (
              <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e0e0e0" }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="error"
                  mb={1}
                >
                  ⚠️ Violation Log
                </Typography>
                <Box
                  sx={{
                    maxHeight: "150px",
                    overflowY: "auto",
                    bgcolor: "#fff3e0",
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid #ffb74d",
                  }}
                >
                  {violationLog.map((violation, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      display="block"
                      sx={{ mb: 0.5, color: "#e65100" }}
                    >
                      <strong>{violation.type}</strong> at {violation.timestamp}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmClose}
        options={{
          title:
            confirmType === "submit"
              ? "Submit Test?"
              : confirmType === "clear"
                ? "Clear All Answers?"
                : "Time's up!",
          text:
            confirmType === "submit"
              ? "Are you sure you want to submit the test?"
              : confirmType === "clear"
                ? "Do you really want to clear all selected answers?"
                : "Time is over! Submit automatically?",
          confirmButtonText: confirmType === "clear" ? "Yes, Clear" : "Submit",
          cancelButtonText: "Cancel",
          confirmButtonColor: confirmType === "clear" ? "error" : "primary",
        }}
      />

      {/* Tab Warning Dialog */}
      <Dialog
        open={showTabWarning}
        onClose={() => setShowTabWarning(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "warning.main", color: "white" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningIcon />
            <span>Warning!</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning">
            <strong>Tab Switch Detected!</strong>
          </Alert>
          <Typography variant="body1" sx={{ mt: 2 }}>
            You have switched tabs during the test. This is your <strong>first warning</strong>.
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            ⚠️ If you switch tabs again, your test will be automatically submitted with current answers.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowTabWarning(false)}
            variant="contained"
            color="warning"
            fullWidth
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={warningMessage.includes("auto-submitted") ? "error" : "warning"}
          sx={{ width: "100%" }}
        >
          {warningMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TestPage;